//
//
// ТODO refactor the entire file. Make it structured.
//
//
// Requirements:
//  1. Circles are clickable.
//  2. Paths are colored.
//  3. YScale becomes logarithmic if linear doesn't fit.
//
import React, { useEffect, useRef } from "react";
import * as d3 from "d3";
import { timeFormatLocale } from 'd3-time-format';
import dayjs from "dayjs";
import { Box, Typography } from "@mui/material";
import { Popper, Paper, Button, Tooltip } from '@mui/material';


import LaunchIcon from '@mui/icons-material/Launch';
import ConstructionIcon from '@mui/icons-material/Construction';

import { route } from "/@/app/routes";
import { formatDate } from "/@/app/utils";
import { useApi} from '/@/app/Api';


// axis locale
var ruRU = {
    dateTime: "%A, %e %B %Y г. %X",
    date: "%d.%m.%Y",
    time: "%H:%M:%S",
    periods: ["AM", "PM"],  // FIXME we don't need AM / PM in Russian locale.
    days: ["воскресенье", "понедельник", "вторник", "среда", "четверг", "пятница", "суббота"],
    shortDays: ["вс", "пн", "вт", "ср", "чт", "пт", "сб"],
    months: ["январь", "февраль", "март", "апрель", "май", "июнь", "июль", "август", "сентябрь", "октябрь", "ноябрь", "декабрь"],
    shortMonths: ["янв", "фев", "мар", "апр", "май", "июн", "июл", "авг", "сен", "окт", "ноя", "дек"]
};
d3.timeFormatDefaultLocale(ruRU);


// Config
const CONF = {
    dotOffset: 25,
    dataPoint: {
        radius: 7,
        radiusCurrentPanel: 10,
    },
    mark: {
        width: 2,
        textSize: 12,
    },
    margin: {
        top: 20, // keeps space for value numbers
        bottom: 32,  // keeps space for mark texts
        // keep space for optimal area numbers
        right: 32,
        left: 32,
    },
    axisOffset: 11,
    colors: {
        point: {
            above: '#e32e2e',
            optimal: '#58a658',
            below: '#5a63ce',
            noReference: '#aaa',
        },
        mark: {
            currentPanel: 'rgba(142,84,243,0.4)',
            currentPanelText: 'rgba(142,84,243,0.9)',
            currentDay: 'rgba(88, 166, 88, 0.7)',
            currentDayText: 'rgb(59,115,59)',
        },
        dimText: {
            above: '#dd5656',
            optimal: '#333',  // '#93d393', Black color asked by Dima V
            below: '#5a63ce',
        },
        optimumArea: {
            fill: "rgba(92, 197, 92, 0.3)",
            numbers: '#58a658',
        },
    }
}

// Calculated config values
CONF.height = CONF.dotOffset * 2 + CONF.dataPoint.radius * 2 + CONF.margin.top + CONF.axisOffset + CONF.margin.bottom;
CONF.baseline = CONF.margin.top + (CONF.height - CONF.margin.top - CONF.margin.bottom - CONF.axisOffset) / 2;


export const Legend = ({ size, sx }) => {
    size = size || 10;

    return (
        <Box sx={{
            display: "flex",
            flexFlow: "row wrap",
            alignItems: "center",
            justifyContent: "center",
            fontSize: `${size}px`,
            mt: 0.5,
            ...sx
        }}>
            <Box sx={{ mr: 1.5, display: "flex", flexFlow: "row nowrap", alignItems: "center" }}>
                <Box sx={{display: "inline-block", width: `${size}px`, height: `${size}px`, backgroundColor: CONF.colors.point.optimal, borderRadius: "50%", m: `${size/2}px`}}></Box>
                <span>- оптимально</span>
            </Box>
            <Box sx={{ mr: 1.5, display: "flex", flexFlow: "row nowrap", alignItems: "center" }}>
                <Box sx={{display: "inline-block", width: `${size}px`, height: `${size}px`, backgroundColor: CONF.colors.point.above, borderRadius: "50%", m: `${size/2}px`}}></Box>
                - выше
            </Box>
            <Box sx={{ mr: 1.5, display: "flex", flexFlow: "row nowrap", alignItems: "center" }}>
                <Box sx={{display: "inline-block", width: `${size}px`, height: `${size}px`, backgroundColor: CONF.colors.point.below, borderRadius: "50%", m: `${size/2}px`}}></Box>
                - ниже
            </Box>
            <Box sx={{ mr: 1.5, display: "flex", flexFlow: "row nowrap", alignItems: "center" }}>
                <Box sx={{display: "inline-block", width: `${size*4}px`, height: `${size}px`, backgroundColor: CONF.colors.optimumArea.fill, m: `${size/2}px`}}></Box>
                - индивидуальная зона оптимума
            </Box>
        </Box>
    )
}


let svgIdCounter = 0;

export const DynamicsChart = ({
                                  data,
                                  fromDate,
                                  tillDate,
                                  hideLegend,
                                  CustomPopperComponent,
                                  CustomNoDataComponent,
                                  currentPanel,
                                  showCurrentDate,
                                  debugTools,
                                  sx
                              }) =>
{

    const api=useApi();

    if (!data) {
        data = [];
    }
    const currentPanelId = currentPanel ? currentPanel.id : null;

    //
    // Chart refresh
    //
    const ref = useRef();

    const [popperAnchor, setPopperAnchor] = React.useState(null);
    const [popperOpen, setPopperOpen] = React.useState(false);
    const [popperData, setPopperData] = React.useState(null);

    // SVG identifier
    const [svgId, _] = React.useState(() => {
        svgIdCounter += 1;
        return svgIdCounter;
    });

    // SVG inner events
    const handleCircleClick = (event, d) => {
        // Highlight the selected circle
        const svgElement = event.currentTarget;
        d3.select(svgElement.closest('svg'))
            .selectAll(".data-point-clickplace")
            .attr("stroke", "transparent");
        d3.select(svgElement).attr("stroke", "orange");

        // Open popper
        setPopperOpen(false);
        setPopperAnchor(event.currentTarget);
        setPopperData(d);
        // FIXME timeout is a hack to recalculate the offset properly between opens
        setTimeout(() => setPopperOpen(true), 0);
    }
    const onPopperClose = () => {
        d3.select(popperAnchor).attr("stroke", "transparent");
        setPopperOpen(false);
    }
    const svgEvents = {
        "circleClick": handleCircleClick,
    }

    // SVG updater
    useEffect(() => {
        const updateSvg = () => { rebuildDiagram(
            ref,
            data,
            svgEvents,
            // use bestCommonSettings() from outside to align all data sets.
            {
                minYearWidth: 100,
                maxYearWidth: 200,
                fromDate: fromDate || null,
                tillDate: tillDate || null,
                currentPanelId: currentPanelId,  // to highlight current panel
                showCurrentDate: !!showCurrentDate,
                svgId: svgId,
            }
        ) };
        updateSvg();

        const observer = new ResizeObserver(() => updateSvg());
        observer.observe(ref.current);

        window.addEventListener('resize', updateSvg);
        return () => {
            window.removeEventListener('resize', updateSvg);
            observer.disconnect();
        }
    }, [data]);

    // put here to catch errors w/o additional user actions.
    const labTestsRoute = route("/lab-tests/");

    const openPanel = () => {
        if (!popperData) {
            return;
        }
        api.request(`cabinet/panels/${popperData.panel_id}/current/`, {}, { method: 'PUT' }
        ).then(data => {
            location.href = labTestsRoute;
        });
    }

    const copyCase = () => {
        // see DebugView/Dynamics.jsx for the case format
        console.log(JSON.stringify(data, null, 4));
    }

    //
    // Display chart and legend
    //
    return (
        <Box sx={{
            display: "flex",
            flexFlow: "column nowrap",
            alignItems: "stretch",
            width: "100%",
            position: "relative",
            ...sx,
        }}>
            {/* debug tools */}
            {debugTools &&
                <Tooltip title="Вывести в консоль кейс для тестов">
                    <Box sx={{
                        position: "absolute", top: "4px", right: "4px", color: "lightgrey"
                    }}>
                        <ConstructionIcon sx={{fontSize: "10px"}} onClick={copyCase} />
                    </Box>
                </Tooltip>
            }

            {/* diagram */}
            <Box component={"svg"} ref={ref} sx={{
                height: CONF.height,
                width: "100%",
            }}></Box>

            {!hideLegend && <Legend />}


            {data.length === 0 ?
                <Box sx={{
                    position: "absolute", top: "20px", left: 0, right: 0, bottom: 0,
                    display: "flex", flexFlow: "column nowrap", alignItems: "center", justifyContent: "flex-start",
                }} >
                    {CustomNoDataComponent
                        ? <CustomNoDataComponent />
                        : <Typography variant="body2" sx={{
                            color: "grey",
                            backgroundColor: "rgba(255,522,522,0.8)",
                            pt: 1, pb: 1, pl: 2, pr: 2,
                            borderRadius: "10px",
                            mt: 1,
                        }}>здесь пока нет данных</Typography>
                    }
                </Box>
                :
                // FIXME detach popper to a separate file.
                <Popper
                    sx={{
                        // On top of everything; FIXME is the number correct?
                        // Track a hierarchy of z-indexes in a separate file.
                        zIndex: 2000,
                    }}
                    open={popperOpen}
                    anchorEl={popperAnchor}
                    placement="bottom"
                    modifiers={[
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 10],
                            },
                        },
                        {
                            name: 'preventOverflow',
                            enabled: true,
                            options: {
                                padding: 4,
                            },
                        },
                        {
                            name: 'flip',
                        }
                    ]}
                >
                    <Paper sx={{
                        backgroundColor: "rgba(241, 242, 251, 0.95)",
                        zIndex: 2000,
                    }}>
                        {CustomPopperComponent &&
                            <CustomPopperComponent measurementId={popperData?.id} closeDialog={()=>{setPopperOpen(false);}} />
                        }
                        {popperData ?
                            <Typography
                                component={"div"}
                                sx={{ p: 2, display: "flex", flexFlow: "column nowrap", alignItems: "stretch" }}
                                variant="body2"
                            >
                                <div>Значение: {popperData.value}</div>
                                {popperData.warning_bottom_edge !== null && popperData.warning_top_edge !== null &&
                                    <div>Индивидуальная Норма: {popperData.warning_bottom_edge > 0.001 ? popperData.warning_bottom_edge : 0} - {popperData.warning_top_edge}</div>
                                }
                                <Box sx={{ alignSelf: "center", pt: 2}}>{formatDate(popperData.made_at)}</Box>
                                {popperData.panel !== null &&
                                    <Box sx={{ alignSelf: "center", pt: 1}}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            color="secondary"
                                            onClick={openPanel}
                                        ><LaunchIcon sx={{ pr: 1 }}/> Перейти к панели</Button>
                                    </Box>
                                }
                            </Typography>
                            : <Typography sx={{ p: 2 }}>No data</Typography>
                        }
                        <Box sx={{ display: "flex", flexFlow: "row nowrap", justifyContent: "center", pb: 1 }}>
                            <Button sx={{ pl: 2, pr: 2 }} size="small" onClick={onPopperClose}>закрыть</Button>
                        </Box>
                    </Paper>
                </Popper>
            }
        </Box>
    )
};


const rebuildDiagram = (ref, data, svgEvents, options) => {
    data = structuredClone(data);  // do not mutate input
    d3.select(ref.current).selectAll('*').remove(); // Clear existing content

    // Settings
    if (options === undefined) options = {};
    const fromDate = options.fromDate || null;
    const tillDate = options.tillDate || null;
    const currentPanelId = options.currentPanelId || null;
    const showCurrentDate = options.showCurrentDate || false;
    const svgId = options.svgId;
    if (!svgId) {
        console.error("svgId is not set");
    }

    let sampleData = false;
    if (data.length === 0) {
        data = EMPTY_CHART_SAMPLE;
        sampleData = true;
        svgEvents = {};
    }

    // Ensure points are sorted.
    // It may be important for some parts of the chart (like curve)
    data = data.sort((a, b) => new Date(a.made_at) - new Date(b.made_at));

    //
    const svg = d3.select(ref.current);
    const defs = svg.append("defs");

    //
    // dim for sample data
    //
    const filterId = `colorFilter-${svgId}`;
    if (sampleData) {
        const filter = defs.append("filter")
            .attr("id", filterId);
        filter.append("feColorMatrix")
            .attr("type", "matrix")
            .attr("values", "0.3 0 0 0 0.7 0 0.3 0 0 0.7 0 0 0.3 0 0.7 0 0 0 1 0");

        svg.attr("filter", `url(#${filterId})`);
    }

    //
    // Calculate start and end
    //
    const dates = data.map(d => new Date(d.made_at));

    // use at least some shift, to get rid of error with single-point data
    const minDateMargin = 1000 * 60 * 60 * 24 * 5; // days
    const maxDateMargin = 1000 * 60 * 60 * 24 * 5; // days
    let minDate;
    if (fromDate) {
        minDate = new Date(fromDate.getTime() - minDateMargin);
    } else {
        minDate = new Date(new Date(d3.min(dates)).getTime() - minDateMargin);
    }
    let maxDate;
    if (tillDate) {
        maxDate = new Date(tillDate.getTime() + maxDateMargin);
    } else {
        maxDate = new Date(new Date(d3.max(dates)).getTime() + maxDateMargin);
    }

    const {width, height} = svg.node().getBoundingClientRect();
    let xAxisWidth = width - CONF.margin.left - CONF.margin.right;
    const numberOfYears = Math.ceil((maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24 * 365));

    const rightAlignOffset = 0
    const yearWidth = xAxisWidth / numberOfYears;

    //
    // x axis
    //
    const baseXScale = d3.scaleTime()
        .domain([minDate, maxDate])
        .range([rightAlignOffset, yearWidth * numberOfYears + rightAlignOffset]);

    const setupXAxis = (a) => {
        return a;  // keep default - it's adaptive.
    }
    svg.append("g")
        .attr("class", "x-axis")
        .attr("transform", `translate(${CONF.margin.left}, ${CONF.height - CONF.margin.bottom})`)
        // .call(xAxis)   // set later
        .attr("color", "#aaa")
        .selectAll("line")
        .attr("y2", function (d, i) {
            return d3.timeYear(d) < d ? 4 : 6; // full year ticks longer
        });

    //
    // Y Scales
    //
    const dValues = data.filter((d) => d.value !== null);
    const valueMin = d3.min(dValues, d => d.value);
    const valueMax = d3.max(dValues, d => d.value);
    const optimumMin = d3.min(
        dValues,
        d => d.warning_bottom_edge !== null ? d.warning_bottom_edge : Infinity
    );
    const optimumMax = d3.max(
        dValues,
        d => d.warning_top_edge !== null ? d.warning_top_edge : -Infinity
    );

    let yScaleDomain = [valueMax, valueMin];
    if (optimumMin !== Infinity && optimumMax !== -Infinity) {
        // ensure optimum is shown, if all values
        // are below / above it.
        if (valueMin > optimumMax) {
            yScaleDomain[1] = optimumMax;
        }
        if (valueMax < optimumMin) {
            yScaleDomain[0] = optimumMin;
        }
        // FIXME domain is flipped!
        // Ensure optimum ends on X axis.
        if (valueMin > optimumMin) {
            yScaleDomain[1] = optimumMin;
        }
    }
    if (valueMax === valueMin) {  // same values case, not suitable for domain
        yScaleDomain[1] = [Math.min(valueMin, optimumMin)]
        yScaleDomain[0] = [Math.max(valueMax, optimumMax)];
    }

    // Range is simply the area above the X scale.
    const yScaleRange = [CONF.margin.top, CONF.height - CONF.margin.bottom - CONF.axisOffset];
    let yScale = d3.scaleLinear()
        .domain(yScaleDomain)
        .range(yScaleRange);


    // decide: linear of logariphmic
    let logScale = false;
    if (yScale(valueMax) < 0) {
        // out of bounds
        logScale = true;
    }

    if (logScale) {
        data = data.map(d => {
            if (d.value === 0) {
                // XXX magic number to keep log(value) defined
                d.value = 0.0001;
            }
            return d;
        })
        yScale = d3.scaleLog()
            .domain(yScaleDomain)
            .range(yScaleRange);
    }

    let yPosition = (d) => {
        // Special case when the optimum area is from 0 to 0. E.g. Frailty index.
        if (
            d.warning_bottom_edge !== null && d.warning_bottom_edge !== null
            && d.warning_bottom_edge <= 0.001 && d.warning_top_edge <= 0.001
            && d.value <= 0.001
        ) {
            // shift to the bottom
            return yScale(d.value) + 6;
        }

        // Normal case
        return yScale(d.value);
    }

    //
    // Prepare Optimum Area Chunks
    //
    // Full curve
    let lineData = data.filter(d => {
        return d.value !== null;
    });

    //
    // Split to horizontal chunks and add lindeData
    //
    let chunkIdx;
    let areaChunks = [];  // {hasReference: true/false, lineData: []}
    let hasReferences = null;
    let currentChunk = [];
    for (const ld of lineData) {
        const _hr = (ld.warning_top_edge !== null && ld.warning_bottom_edge !== null);
        if (_hr !== hasReferences) {
            if (currentChunk.length) {
                areaChunks.push({hasReferences: hasReferences, lineData: currentChunk});
            }
            currentChunk = [];
        }
        hasReferences = _hr;
        currentChunk.push(ld);
    }
    if (currentChunk.length) {  // add the last one
        areaChunks.push({hasReferences: hasReferences, lineData: currentChunk});
    }

    //
    // Optimum Area
    //
    chunkIdx = 0;
    for (const chunk of areaChunks) {
        chunkIdx += 1;
        chunk.areaData = chunk.lineData.toSorted((a, b) => new Date(a.made_at) - new Date(b.made_at));
        //
        // Add 1st and last shifted points to close the area.
        // They can be updated on the fly, to fit `marginPixels` on screen.
        //
        const updateMarginItems = (xScale) => {
            let leftMarginItem = null;
            let rightMarginItem = null;
            if (chunk.areaData.length > 0) {
                // Calculate time
                const totalTime = maxDate.getTime() - minDate.getTime();
                const totalTimeInPixels = xScale(maxDate) - xScale(minDate);
                const marginPixels = 15; // FIXME on zoom it may become too wide. But it's ok for now.
                const marginTime = totalTime / totalTimeInPixels * marginPixels;

                // remove items
                chunk.areaData = chunk.areaData.filter(d => !d.corner);
                const ad = chunk.areaData;

                // add items
                leftMarginItem = {
                    "made_at": new Date(new Date(ad[0].made_at).getTime() - marginTime).toISOString(),
                    "warning_bottom_edge": ad[0].warning_bottom_edge,
                    "warning_top_edge": ad[0].warning_top_edge,
                    "corner": true,  // will show some numbers on corners
                    "cornerLeft": true,
                }
                chunk.areaData.unshift(leftMarginItem);
                rightMarginItem = {
                    "made_at": new Date(new Date(ad[ad.length - 1].made_at).getTime() + marginTime).toISOString(),
                    "warning_bottom_edge": ad[ad.length - 1].warning_bottom_edge,
                    "warning_top_edge": ad[ad.length - 1].warning_top_edge,
                    "corner": true,  //
                    "cornerRight": true,
                }
                chunk.areaData.push(rightMarginItem);
            }
            return [leftMarginItem, rightMarginItem].filter(v => v !== null);
        }

        chunk.updateMarginItems = (_) => {};
        if (chunk.hasReferences) {
            chunk.updateMarginItems = updateMarginItems;
            //
            // Append the area to the SVG
            //
            svg.append("path")
                .datum(chunk.areaData)
                .attr("class", `optimum-area-chunk${chunkIdx}`)
                .attr("fill", CONF.colors.optimumArea.fill)
                .attr("transform", `translate(${CONF.margin.left}, 0)`);
        }
    }

    //
    // Current Panel Circle & line - below curves
    //
    svg.append("g")
        .attr("transform", `translate(${CONF.margin.left},0)`)
        .selectAll("circle.data-point-current-panel")
        .data(data.filter(d => currentPanelId && d.panel_id === currentPanelId))
        .enter()
        .append("circle")
        .attr("class", "data-point-current-panel")
        .attr("cy", yPosition)
        .attr("r", CONF.dataPoint.radiusCurrentPanel)
        .attr("fill", CONF.colors.mark.currentPanel)
        .attr("stroke-width", 0)
    // vertical line
    svg.append("g")
        .attr("transform", `translate(${CONF.margin.left},0)`)
        .selectAll("rect.mark-current-panel")
        .data(data.filter(d => currentPanelId && d.panel_id === currentPanelId))
        .enter()
        .append("rect")
        .attr("class", "mark-current-panel")
        .attr("fill", CONF.colors.mark.currentPanel)
        .attr("width", CONF.mark.width)
        .attr("height", d => CONF.height - CONF.mark.textSize - 2 - yPosition(d))
        .attr("y", yPosition)
    // text
    svg.append("g")
        .attr("transform", `translate(${CONF.margin.left},0)`)
        .selectAll("text.mark-current-panel")
        .data(data.filter(d => currentPanelId && d.panel_id === currentPanelId))
        .enter()
        .append("text")
        .text("текущая панель")
        .attr("text-anchor", "middle")
        .attr("font-size", CONF.mark.textSize + "px")
        .attr("class", "mark-current-panel")
        .attr("fill", CONF.colors.mark.currentPanelText)
        .attr("y", CONF.height - 3)

    //
    // This day - vertical line
    //
    if (showCurrentDate && dayjs().isAfter(dayjs(minDate).add(20, 'days'))) {
        svg.append("g")
            .attr("transform", `translate(${CONF.margin.left},0)`)
            .selectAll("rect.mark-current-date")
            .data([{made_at: dayjs().toISOString()}])  // today
            .enter()
            .append("rect")
            .attr("class", "mark-current-date")
            .attr("fill", CONF.colors.mark.currentDay)
            .attr("width", CONF.mark.width)
            .attr("height", CONF.height - CONF.margin.bottom / 2)
            .attr("y", 0)

        svg.append("g")
            .attr("transform", `translate(${CONF.margin.left},0)`)
            .selectAll("text.mark-current-date")
            .data([{made_at: dayjs().toISOString()}])  // today
            .enter()
            .append("text")
            .text("сегодня")
            .attr("text-anchor", "middle")
            .attr("font-size", CONF.mark.textSize + "px")
            .attr("class", "mark-current-date")
            .attr("fill", CONF.colors.mark.currentDayText)
            .attr("y", CONF.height - CONF.mark.textSize / 2)
    }

    //
    // Prepare 3 curves for clip paths: above, below, middle, OR full height.
    //
    let noRefFound = false;
    chunkIdx = 0;
    for (const chunk of areaChunks) {
        chunkIdx += 1;
        chunk.colorSections = [];
        if (chunk.hasReferences) {
            chunk.colorSections.push("optimal");
            if (chunk.areaData.length > 0) {
                chunk.colorSections.push(...[
                    "above",
                    "below",
                ]);
            }
        } else {
            noRefFound = true;
            chunk.colorSections.push("noReference");
        }
    }

    //
    // Underlying single curve for points w/o reference.
    //
    if (noRefFound) {
        svg.append('path')
            .attr("class", `colored-curve-noReference`)
            .datum(lineData)
            .attr("transform", `translate(${CONF.margin.left},0)`)
            .attr('fill', 'none')
            .attr('stroke-width', 6)
            .attr('stroke', CONF.colors.point.noReference);
    }

    //
    // Line & clip paths for chunks
    //
    chunkIdx = 0;
    for (const chunk of areaChunks) {
        chunkIdx += 1;
        for (let section of chunk.colorSections) {
            if (section === 'noReference') {
                continue; // underlying single line used
            }
            const clipPathId = `clip-path-${svgId}-${section}-chunk${chunkIdx}`;

            defs.append('clipPath')
                .attr("id", clipPathId)
                .append('path')
                .datum(chunk.areaData)


            svg.append('path')
                .attr("class", `colored-curve-${section}-chunk${chunkIdx}`)
                .datum(lineData)
                .attr("transform", `translate(${CONF.margin.left},0)`)
                .attr('clip-path', d => chunk.areaData.length > 0 ? `url(#${clipPathId})` : '')
                .attr('fill', 'none')
                .attr('stroke-width', 6);
        }
    }


    //
    // Circles - above curves
    //
    svg.append("g")
        .attr("transform", `translate(${CONF.margin.left},0)`)
        .selectAll("circle.data-point")
        .data(data)
        .enter()
        .append("circle")
        .attr("class", "data-point")
        .attr("cy", yPosition)
        .attr("r", CONF.dataPoint.radius)
        .attr("fill", getCircleColor)
        .attr("stroke", "white")
        .attr("stroke-width", d => 2);


    //
    // Numbers
    //
    svg.append("g")
        .attr("transform", `translate(${CONF.margin.left},0)`)
        .selectAll("text.data-number")
        .data(data)
        .enter()
        .append("text")
        .attr("class", "data-number")
        .attr("y", d => {
            return yPosition(d) - 10;
        } )
        .text(d => {
            let text = d.value;
            if (d.value <= 0.001) {
                text = 0;
            }
            text = Math.round(text * 100) / 100;
            return text;
        })
        .attr("text-anchor", "middle") // Center the text above the circle
        .attr("fill", getLabelsColor)
        .attr("font-size", "10px"); // Text size

    chunkIdx = 0;
    for (const chunk of areaChunks) {
        chunkIdx += 1;
        const marginItems = chunk.updateMarginItems(baseXScale);
        if (!marginItems) {
            continue;
        }
        //
        // Numbers for optimum area corners
        //
        const optNumbers = svg.append("g")
            .attr("transform", `translate(${CONF.margin.left},0)`)
            .selectAll("text.data-optimum-number")
            .data(marginItems)
            .enter()
            .append("text")
            .attr("class", "data-optimum-number")
            .attr("text-anchor", d => d.cornerLeft ? "end" : "start")
            .attr("fill", CONF.colors.optimumArea.numbers)
            .attr("font-size", "10px") // Text size

        const optNumbersTop = optNumbers.clone()
        optNumbersTop
            .attr("class", `data-optimum-number data-optimum-number--top-chunk${chunkIdx}`)
            .text(d => {
                let text = Math.round(d.warning_top_edge * 10) / 10;
                return text;
            })
            .attr("y", d => {
                let base = yScale(d.warning_top_edge);
                if (base < 4) {
                    // sometimes the green area can be out of vertical range
                    base = 4;
                }
                return base + 4;
            })
        const optNumbersBottom = optNumbers.clone()
        optNumbersBottom
            .attr("class", `data-optimum-number data-optimum-number--bottom-chunk${chunkIdx}`)
            .text(d => {
                if (d.warning_top_edge < 0.001) {
                    // frailty index issue: from 0 to 0, do not show the bottom edge
                    return "";
                }
                let text = Math.round(d.warning_bottom_edge * 10) / 10;
                return text;
            })
            .attr("y", d => {
                if (d.warning_bottom_edge <= 0.001) {
                    // If bottom edge is zero, span the area to the bottom.
                    return CONF.height - CONF.margin.bottom - 2;
                }
                return yScale(d.warning_bottom_edge) + 4;
            })
    }


    //
    // Circle click catchers.
    // 1. On top of everything.
    // 2. Slightly bigger than circles themselves.
    //
    svg.append("g")
        .attr("transform", `translate(${CONF.margin.left},0)`)
        .selectAll("circle.data-point-clickplace")
        .data(data)
        .enter()
        .append("circle")
        .attr("class", "data-point-clickplace")
        .style("cursor", "pointer")
        .attr("cy", yPosition)
        .attr("r", CONF.dataPoint.radius * 1.5)
        .attr("fill", "transparent")
        .attr("stroke", "transparent")
        .attr("stroke-width", 2)
        .on('click', svgEvents.circleClick);


    //
    // Set X axis to all elements. Used in zoom and by default.
    //
    const setZoomedXAxis = (transform) => {
        let chunkIdx;
        let xScale = baseXScale;
        if (transform) {
            xScale = transform.rescaleX(baseXScale);
        }

        //
        //
        //
        svg.selectAll(".data-point-clickplace, .data-point-current-panel").attr(
            "cx",
            d => xScale(new Date(d.made_at))
        );
        svg.selectAll("rect.mark-current-panel, rect.mark-current-date").attr(
            "x",
            d => xScale(new Date(d.made_at)) - CONF.mark.width / 2
        );
        svg.selectAll("text.mark-current-panel, text.mark-current-date").attr(
            "x",
            d => xScale(new Date(d.made_at))
        );

        // dots
        svg.select(".x-axis").call(
            setupXAxis(d3.axisBottom(xScale))
        );
        svg.selectAll(".data-point").attr(
            "cx",
            d => xScale(new Date(d.made_at))
        );
        svg.selectAll(".data-number").attr(
            "x",
            d => xScale(new Date(d.made_at))
        );


        chunkIdx = 0;
        for (const chunk of areaChunks) {
            chunkIdx += 1;
            // Update left and right "fake" data points to areas that create margins
            // and set the data.
            const marginItems = chunk.updateMarginItems(xScale);
            if (!marginItems) {
                continue;
            }
            //
            // Opt area corner numbers.
            // Somehow, we need to update top & bottom separately, to display proper X position.
            //
            svg
                .selectAll(`text.data-optimum-number--top-chunk${chunkIdx}`)
                .data(marginItems)  // the data with updated margins
                .attr("x", d => {
                        const base = xScale(new Date(d.made_at))
                        if (d.cornerLeft) {
                            return base - 2;
                        } else {
                            return base + 2;
                        }
                    }
                );
            svg
                .selectAll(`text.data-optimum-number--bottom-chunk${chunkIdx}`)
                .data(marginItems)  // the data with updated margins
                .attr("x", d => {
                        const base = xScale(new Date(d.made_at))
                        if (d.cornerLeft) {
                            return base - 2;
                        } else {
                            return base + 2;
                        }
                    }
                );
        }
        chunkIdx = 0;
        for (const chunk of areaChunks) {
            chunkIdx += 1;
            //
            // Optimum area green field
            //
            const areaGenerator = d3.area()
                .x(d => xScale(new Date(d.made_at)))
                .y0(d => {
                    if (d.warning_bottom_edge <= 0.001) {
                        // If bottom edge is zero, span the area to the bottom.
                        return CONF.height - CONF.margin.bottom;
                    }
                    return yScale(d.warning_bottom_edge)
                })
                .y1(d => yScale(d.warning_top_edge));
            svg
                .select(`.optimum-area-chunk${chunkIdx}`)
                .datum(chunk.areaData)  // the data with updated margins
                .attr("d", areaGenerator);
            chunk.areaGenerator = areaGenerator;
        }

        //
        // Painting the curve
        //
        const lineGenerator = d3.line()
            .x(d => xScale(new Date(d.made_at)))
            .y(yPosition)
            .curve(d3.curveMonotoneX); // This makes the line curved. May try different curves.

        //
        // Underlying single curve for points w/o reference.
        //
        svg.selectAll(`.colored-curve-noReference`)
            .attr('d', lineGenerator)
        //
        // Clip paths for the curve
        //
        chunkIdx = 0;
        for (let chunk of areaChunks) {
            chunkIdx += 1;
            for (let section of chunk.colorSections) {
                if (section === 'noReference') {
                    continue;  // underlying single line used.
                }
                const clipPathId = `clip-path-${svgId}-${section}-chunk${chunkIdx}`;

                const areaGenerator = chunk.areaGenerator;
                const clipArea = {
                    "optimal": d3.area().x(areaGenerator.x()).y0(areaGenerator.y0()).y1(areaGenerator.y1()),
                    "above": d3.area().x(areaGenerator.x()).y0(d => 0).y1(areaGenerator.y1()),
                    "below": d3.area().x(areaGenerator.x()).y0(areaGenerator.y0()).y1(d => CONF.height),
                }[section];

                const color = {
                    "optimal": CONF.colors.point.optimal,
                    "above": CONF.colors.point.above,
                    "below": CONF.colors.point.below,
                }[section];

                defs.select(`#${clipPathId} path`)
                    .attr('d', clipArea);
                svg.selectAll(`.colored-curve-${section}-chunk${chunkIdx}`)
                    .attr('d', lineGenerator)
                    .attr('stroke', color);
            }
        }
    }

    setZoomedXAxis(null);

    //
    // Zoom behavior
    //
    const zoom = d3.zoom()
        //        .scaleExtent([1, 3])  // Limit zoom scale
        .translateExtent([[0, 0], [width, height]])
        .extent([[0, 0], [width, height]])
        .on("zoom", (event) => {
            const { transform } = event;
            setZoomedXAxis(transform);
        })
        .filter(event => {
            // Distinguish between mouse and touch events directly
            if (event.type.startsWith('mouse')) {
                // Allow all mouse events for horizontal pan
                return true;
            } else if (event.type === 'touchstart' || event.type === 'touchmove') {
                // For touch events, allow only if there's more than one touch point (e.g., for zooming)
                return event.touches.length > 1;
            } else if (event.type === 'wheel') {
                return true;
            }
            // Default to not allowing the event
            return false;
        });
    svg.call(zoom);
};


const getCircleColor = (d) => {
    if (d.value === null) return "transparent"; // just hide it
    //
    if (d.warning_top_edge === null || d.warning_bottom_edge === null) {
        return CONF.colors.point.noReference;
    } else {
        if (d.value > d.warning_top_edge) return CONF.colors.point.above;
        if (d.value <= d.warning_bottom_edge) {
            if (d.warning_bottom_edge <= 0.001) {
                // bottom edge is zero, consider it optimal
                return CONF.colors.point.optimal;
            }
            // normal bottom area
            return CONF.colors.point.below;
        }
    }
    return CONF.colors.point.optimal;
}

const getLabelsColor = (d) => {
    if (d.warning_top_edge === null || d.warning_bottom_edge === null) {
        return CONF.colors.dimText.optimal;
    }
    if (d.value > d.warning_top_edge) return CONF.colors.dimText.above;
    if (d.value <= d.warning_bottom_edge) {
        if (d.warning_bottom_edge <= 0.001) {
            // bottom edge is zero, consider it optimal
            return CONF.colors.dimText.optimal;
        }
        // normal bottom area
        return CONF.colors.dimText.below;
    }
    return CONF.colors.dimText.optimal;
}



//
// Visible to all users when there's no data.
//
const EMPTY_CHART_SAMPLE = [
    {
        "panel_id": null,
        "made_at": "2021-01-01T18:01:00Z",
        "value": 2.0,
        "warning_bottom_edge": 0.001,
        "warning_top_edge": 2.59
    },
    {
        "panel_id": null,
        "made_at": "2021-06-01T18:01:00Z",
        "value": 2.0,
        "warning_bottom_edge": 0.001,
        "warning_top_edge": 2.59
    },
    {
        "panel_id": null,
        "made_at": "2021-12-31T18:01:00Z",
        "value": 3.2,
        "warning_bottom_edge": 0.001,
        "warning_top_edge": 2.59
    },
    {
        "panel_id": null,
        "made_at": "2022-06-30T18:01:00Z",
        "value": 2.0,
        "warning_bottom_edge": 0.001,
        "warning_top_edge": 2.9
    },
    {
        "panel_id": null,
        "made_at": "2022-12-31T18:01:00Z",
        "value": 1.66,
        "warning_bottom_edge": 0.001,
        "warning_top_edge": 2.9
    },
    {
        "panel_id": null,
        "made_at": "2023-06-30T18:01:00Z",
        "value": 3.0,
        "warning_bottom_edge": 0.001,
        "warning_top_edge": 2.59
    }
]
